
import React from 'react';
import { UserProfile } from '@/types';
import { Link } from 'react-router-dom';
import { BarChart2, Video, Radio, Music, Share2, BookOpen, Calendar, ChevronRight, BarChart } from 'lucide-react';
import { Button } from '@/components/ui/button';

import StreamingPerformanceChart from './charts/StreamingPerformanceChart';
import RevenueBreakdownChart from './charts/RevenueBreakdownChart';
import GeographyChart from './charts/GeographyChart';
import PlatformDistributionChart from './charts/PlatformDistributionChart';
import AudienceMetricsChart from './charts/AudienceMetricsChart';
import AIPoweredInsights from './AIPoweredInsights';
import SocialMediaSection from './sections/SocialMediaSection';
import LiveStreamingSection from './sections/LiveStreamingSection';
import ReleasesSection from './sections/ReleasesSection';
import RadioSection from './sections/RadioSection';
import ChartAlerts from './ChartAlerts';

import { 
  generateStreamingData, 
  generateRevenueData, 
  generateDemographicData, 
  generatePlatformData, 
  generateAudienceMetrics,
  generateInsights
} from './utils/dataGenerators';

interface AnalyticsVisualizationsProps {
  timeRange: string;
  userProfile?: UserProfile;
}

const AnalyticsVisualizations: React.FC<AnalyticsVisualizationsProps> = ({ timeRange, userProfile }) => {
  const streamingData = generateStreamingData(timeRange);
  const revenueData = generateRevenueData(timeRange);
  const demographicData = generateDemographicData();
  const platformData = generatePlatformData();
  const audienceMetrics = generateAudienceMetrics();
  const insights = generateInsights(timeRange);
  
  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart className="h-5 w-5 text-soundcharts-purple" />
            Chart Performance
          </h2>
        </div>
        <ChartAlerts />
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Music className="h-5 w-5 text-soundcharts-purple" />
            Music Streaming Analytics
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/releases" className="gap-1 text-sm">
              <span>View Releases</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StreamingPerformanceChart streamingData={streamingData} />
          <RevenueBreakdownChart revenueData={revenueData} />
        </div>
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-soundcharts-purple" />
            Audience Demographics
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <GeographyChart demographicData={demographicData} />
          <PlatformDistributionChart platformData={platformData} />
          <AudienceMetricsChart audienceMetrics={audienceMetrics} />
        </div>
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Video className="h-5 w-5 text-soundcharts-purple" />
            Live Streaming Analytics
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/live-streaming" className="gap-1 text-sm">
              <span>View Live Streaming</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <LiveStreamingSection timeRange={timeRange} />
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Share2 className="h-5 w-5 text-soundcharts-purple" />
            Social Media Performance
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/marketing" className="gap-1 text-sm">
              <span>View Marketing</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <SocialMediaSection timeRange={timeRange} />
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-soundcharts-purple" />
            Releases Overview
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/releases" className="gap-1 text-sm">
              <span>Manage Releases</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <ReleasesSection />
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Radio className="h-5 w-5 text-soundcharts-purple" />
            Radio Airplay
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/radio" className="gap-1 text-sm">
              <span>View Radio Dashboard</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <RadioSection timeRange={timeRange} />
      </section>
      
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-soundcharts-purple" />
            AI-Powered Insights
          </h2>
        </div>
        <AIPoweredInsights insights={insights} />
      </section>
    </div>
  );
};

export default AnalyticsVisualizations;
